<template>
  <!-- SOLUTIONS & PARTNERS SUBHEADER LINKS -->
  <div class="font-rubik w-full flex flex-col lg:flex-row justify-center lg:pb-[16px] lg:pt-20 border border-b border-gray-300 lg:border-0 bg-themeComponentBg-light">
    <div class="subheader-inner-container w-full max-w-1216 bg-white">
      <div class="relative">
        <!-- Cover Background Image -->
        <img
          :src="computedBackgroundImage"
          alt="Background"
          class="subheader-cover-img w-full lg:rounded-t-lg"
        />
      </div>
      <!-- Navigation Tabs -->
      <div :class="['tabs-container flex flex-col justify-between', { 'expended-min-height': !isExpanded && isMobile && fullTextText.length > 143 }]">
        <div class="w-full flex flex-row justify-between h-[80px] lg:h-[105px]">
          <div class="subheader-logo-container">
            <img class="subheader-logo-img" :src="organizationLogo" :alt="organization.name">
          </div>
          <div class="subheader-partner-logo-img">
            <span class="partner-badge" v-html="caBadgeSvg"></span>
          </div>
        </div>
        <div class="w-full h-auto main-text-container">
          <h2 class="subheader-area-name pt-0 lg:pt-4 pb-2">{{ organization.name }}</h2>
          <p v-if="organization.data['service_area']" class="subheader-serving-area pb-3">Serving container solutions at {{organization.data['service_area'].trimEnd() }}.</p>
          <div class="subheader-serving-area">
            <template v-if="isMobile">
              <template v-if="isExpanded">
                <span v-html="fullTextHtml"></span>
              </template>
              <template v-else>
                {{ truncatedText }}
                <span v-if="!isExpanded && fullTextText.length > 143">
                  <p class="font-semibold" @click.prevent="toggleExpand">read more...</p>
                </span>
              </template>
            </template>
            <template v-else>
              <span v-html="fullTextHtml"></span>
            </template>
          </div>
        </div>
        <!-- Contact and Button -->
        <div class="flex items-center lg:justify-start justify-between gap-4 flex-row-reverse lg:flex-row contact-button-container">
          <button 
            class="get-quote-button border rounded-full flex items-center justify-center"
            data-vue-init="lead-flow" 
            data-props='{ "gtag": "get_quote"}'  
            :style="{ backgroundColor: brandColor, borderColor: brandColor }"
          >
            <p>Get Quote</p>
          </button>
          <a 
            v-if="organization.phone"
            :href="`tel:${organization.data.phone}`" 
            class="subheader-phone hidden lg:inline-block" 
          >
            {{ formatPhoneNumber(organization.phone) }}
          </a>
          <a 
            :href="`tel:${organization.data.phone}`" 
            class="subheader-phone inline-block lg:hidden underline" 
          >
            Call Us
          </a>
        </div>
        <div class="hidden lg:flex lg:flex-row flex-col justify-between lg:items-center h-[44px]">
          <!-- Desktop Nav -->
          <div class="hidden lg:flex items-center space-x-6 h-full">
            <!-- Static link for 'About' -->
            <div 
              :class="['nav-link', isActive(aboutSlug) ? 'active' : '', 'flex items-center px-3 h-full']"
              :style="isActive(aboutSlug) ? { '--active-color': brandColor } : {}"
            >
              <a 
                :href="aboutSlug" 
                class="subheader-nav-links mt-1" 
                :style="isActive(aboutSlug) ? { color: brandColor } : { color: '#0c0e20' }"
              >
                About
              </a>
            </div>
            <!-- Dynamic links from solutionsArr -->
            <div 
              v-for="solution in solutionsArr" :key="solution.slug"
              :class="['nav-link', isActive(solution.slug) ? 'active' : '', 'flex items-center px-3 h-full']"
              :style="isActive(solution.slug) ? { '--active-color': brandColor } : {}"
            >
              <a 
                :href="solution.slug" 
                class="subheader-nav-links" 
                :style="isActive(solution.slug) ? { color: brandColor } : { color: '#0c0e20' }"
              >
              {{ solution.title }}
            </a>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Mobile Nav -->
    <div class="flex flex-row justify-between lg:hidden text-gray-600 px-4 pt-8 bg-white">
      <div
        v-for="link in mobileMainLinks"
        :key="link.slug"
        :class="['nav-link', isActive(link.slug) ? 'active' : '', 'flex items-center px-2 h-full']"
        :style="isActive(link.slug) ? { '--active-color': brandColor } : {}"
      >
        <a
          :href="link.slug"
          class="subheader-nav-links mt-1"
          :style="isActive(link.slug) ? { color: brandColor } : { color: '#0c0e20' }"
        >
          {{ link.label }}
        </a>
      </div>
      <div v-if="mobileMoreLinks.length > 0">
        <Dropdown :page="moreDropdownPage" />
      </div>
    </div>
  </div>
</template>

<script>
import caBadgeSvg from '@/images/header/ca-badge.svg?raw'
import bgWide from '@/images/subheader/container_wide.png'
import bgSquare from '@/images/subheader/container_square.png'
import { formatPhoneNumber } from '../../shared/utils'
import Dropdown from './helpers/dropdown.vue'
export default {
  name: 'ContainerHeader',
  components: {
    Dropdown
  },
  props: {
    organization: {
      required: true,
      type: Object
    },
    solutions: {
      required: true,
      type: Array
    },
    solutionsPath: {
      type: Object,
      required: true
    },
    backgroundImage: {
      required: true,
      type: String
    },
    organizationLogo: {
      required: true,
      type: String
    }
  },
  data() {
    return {
      caBadgeSvg,
      solutionsArr: [],
      aboutSlug: '/solutions',
      keywordMappings: [
        { keyword: 'refrigerated container rentals', output: 'Refrigerated Rentals' },
        { keyword: 'refrigerated containers', output: 'Refrigerated Containers' },
        { keyword: 'container modifications', output: 'Modifications' },
        { keyword: 'portable offices', output: 'Portable Offices' },
        { keyword: 'accessories', output: 'Accessories' },
        { keyword: 'container sales', output: 'Sales' },
        { keyword: 'sales', output: 'Sales' },
        { keyword: 'rentals', output: 'Rentals' },
        { keyword: 'modifications', output: 'Modifications' },
        { keyword: 'portable', output: 'Portable Offices' },
        { keyword: 'refrigerated', output: 'Refrigerated Container' }
      ],
      isExpanded: false,
      isMobile: false,
      fullTextHtml: 'The Container Alliance is a network of experienced portable storage and shipping container providers that helps customers find the best quality containers at competitive prices. They offer a vendor matching system to connect users with local suppliers for renting or purchasing containers, including 20 ft and 40 ft options. Based in Santa Barbara, CA, they provide resources and quotes through their website for businesses seeking container solutions.',
      fullTextText: 'The Container Alliance is a network of experienced portable storage and shipping container providers that helps customers find the best quality containers at competitive prices. They offer a vendor matching system to connect users with local suppliers for renting or purchasing containers, including 20 ft and 40 ft options. Based in Santa Barbara, CA, they provide resources and quotes through their website for businesses seeking container solutions.'
    }
  },
  computed: {
    computedBackgroundImage() {
      if (this.backgroundImage) {
        return this.backgroundImage
      } else {
        return this.isMobile ? bgSquare : bgWide
      }
    },
    truncatedText() {
      return this.fullTextText.slice(0, 143)
    },
    currentUrl() {
      return window.location.pathname.replace(/\/$/, '')
    },
    brandColor() {
      return this.organization.data.brand_color || '#4D84FF'
    },
    mobileMainLinks() {
      const links = [{ slug: this.aboutSlug, label: 'About' }]
      const maxMainLinks = 3
      const numAdditionalLinks = maxMainLinks - 1
      const solutionLinks = this.solutionsArr.map(s => ({
        slug: s.slug,
        label: s.title
      }))
      for (let i = 0; i < numAdditionalLinks && i < solutionLinks.length; i++) {
        links.push(solutionLinks[i])
      }
      return links
    },
    mobileMoreLinks() {
      const mainLinkSlugs = this.mobileMainLinks.map(link => link.slug)
      return this.solutionsArr.filter(s => !mainLinkSlugs.includes(s.slug))
    },
    moreDropdownPage() {
      return {
        name: 'More',
        pages: this.mobileMoreLinks.map(s => ({
          name: s.title,
          slug: s.slug
        }))
      }
    }
  },
  mounted() {    
    if (this.organization.teaser) {
      this.fullTextHtml = this.organization.teaser
      this.fullTextText = this.stripHTML(this.organization.teaser)
    }
    this.createSolutionsArray(this.solutions)
    this.saveServiceAreaAboutUrl()
    if (this.currentUrl.includes('/partners/')) {
      this.aboutSlug = `/partners/${this.organization.slug}`
    } else {
      const storedSlug = localStorage.getItem('serviceAreaAboutSlug')
      this.aboutSlug = storedSlug ? JSON.parse(storedSlug) : '/solutions'
    }    
  },
  created() {
    this.updateIsMobile()
    window.addEventListener('resize', this.updateIsMobile)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateIsMobile)
  },
  methods: {
    formatPhoneNumber,
    stripHTML(html) {
      return html.replace(/<[^>]*>/g, '').trim()
    },
    updateIsMobile() {
      this.isMobile = window.innerWidth <= 768
    },
    toggleExpand() {
      this.isExpanded = true
    },
    createSolutionsArray(solutions) {
      this.solutionsArr = solutions.map(solution => {
        const formattedTitle = this.formatTitle(solution.title)
        const slug = this.getSolutionSlug(solution.id)
        return {
          title: formattedTitle,
          slug: slug
        }
      })
    },
    formatTitle(title) {
      const lowerTitle = title.toLowerCase()
      for (const mapping of this.keywordMappings) {
        if (lowerTitle.includes(mapping.keyword.toLowerCase())) {
          return mapping.output
        }
      }
      return title.replace(/\b\w/g, l => l.toUpperCase())
    },
    getSolutionSlug(id) {
      return this.solutionsPath[id.toString()] || '#'
    },
    isActive(slug) {
      const currentUrl = window.location.pathname.replace(/\/$/, '')
      return currentUrl === slug.replace(/\/$/, '')
    },
    saveServiceAreaAboutUrl() {
      if (this.currentUrl.includes('/service-areas/') && this.validServiceAreaAboutSlug()) {
        localStorage.setItem('serviceAreaAboutSlug', JSON.stringify(this.currentUrl))
      }
    },
    validServiceAreaAboutSlug () {
      const regex = /^\/service-areas\/[^/]+\/[^/]+\/?$/
      return regex.test(this.currentUrl)
    },
    baseUrl() {
      return `/partners/${this.organization.slug}`
    }
  }
}
</script>

<style scoped>
.nav-link {
  position: relative;
}

.nav-link.active::before {
  content: "";
  position: absolute;
  bottom: 0px;
  left: 50%;
  transform: translateX(-50%);
  width: 48px;
  height: 4px;
  border-top-left-radius: 5px;
  border-top-right-radius: 5px;
  background-color: var(--active-color, transparent);
  transition: background-color 0.3s ease;
}

.subheader-inner-container {
  position: relative;
  border: 2px solid #E2E1DE;
  border-radius: 12px;
  background-color: white;
}

.subheader-cover-img {
  height: 240px;
  object-fit: cover;
}

.subheader-logo-container {
  position: absolute;
  top: 180px;
  left: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 164px;
  height: 164px;
  background-color: white;
  border: 1px solid #E2E1DE;
  border-radius: 50%;
}

.subheader-logo-img {
  width: 100px;
  height: 100px;
  object-fit: contain;
}

.subheader-partner-logo-img {
  position: absolute;
  top: 185px;
  right: 32px;
}

.subheader-partner-logo-img img {
  object-fit: contain;
  height: 180px
}

.partner-badge {
  width: auto;
  height: auto;
  display: inline-block;
  object-fit: contain;
}

.partner-badge svg {
  width: 100%;
  height: 100%;
}

.subheader-area-name {
  color: #334155;
  font-size: 24px;
  font-weight: 400;
  line-height: 28.8px;
  letter-spacing: -0.04em;
}

.subheader-serving-area {
  color: #334155;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
}

.tabs-container {
  height: auto;
  padding: 0px 32px;
}

.subheader-phone {
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.04em;
  color: #334155 !important;
}

.get-quote-button {
  width: 113px;
  height: 44px;
}

.get-quote-button p {
  color: #FFF;
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: -0.04em;
}

.serving-text {
  font-weight: 300 !important;
  color: #666666;
  padding-right: 3px;
}

.subheader-nav-links {
  text-align: center;
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
  letter-spacing: -0.0125em;
}

.subheader-nav-links:hover {
  color: #4D84FF !important;
}

.subheader-org-title {
  font-size: 24px;
  font-weight: 400;
  line-height: 28.8px;
  letter-spacing: -0.04em;
  color: black;
}

.expended-min-height {
  min-height: 310px;
}

.main-text-container {
  padding: 16px 0px;
}

.contact-button-container {
  padding: 16px 0px;
}

@media(max-width: 1024px) {
  .subheader-cover-img {
    height: 240px;
    object-fit: fill;
  }

  .subheader-inner-container {
    border: 0px;
    border-radius: 0px;
    background-color: white;
  }

  .tabs-container {
    min-height: 320px;
    height: auto;
    padding: 0px 16px;
  }

  .subheader-area-name {
    color:  #334155;
    font-size: 24px;
    font-weight: 400;
    line-height: 28.8px;
    letter-spacing: -0.04em;
  }

  .subheader-logo-container {
    top: 205px;
    left: 16px;
    width: 108px;
    height: 108px;
  }

  .subheader-logo-img {
    width: 70px;
    height: 70px;
  }

  .subheader-partner-logo-img {
    position: absolute;
    top: 200px;
    /* right: 32px; */
    width: 87px;
    height: 120px;
  }

  .subheader-partner-logo-img img {
    object-fit: contain;
    height: 125px
  }

  .partner-badge {
    width: 110px !important;
    height: 180px !important;
  }

  .partner-badge svg {
    width: 100%;
    height: 100%;
  }

  .subheader-nav-links {
    text-align: center;
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.0125em;
    padding-bottom: 8px;
    color: black !important;
  }

  .get-quote-button {
    width: 155.5px;
    height: 44px;
  }

  .get-quote-button p {
    color: #FFF;
    font-size: 16px;
    font-weight: 500;
    line-height: 24px;
    letter-spacing: -0.04em;
  }

  .main-text-container {
    padding: 0px 0px;
  }
  
  .contact-button-container {
    padding: 8px 0px;
  }
}

.subheader-partner-logo-img img {
  transition: transform 0.3s ease;
}

@media (max-width: 768px) {

  .subheader-partner-logo-img {
    right: 45px;
  }

  .subheader-partner-logo-img img {
    transform: none;
  }

  .subheader-partner-logo-img .partner-badge {
    transform: scale(0.7);
    transform-origin: top right;
  }
}
</style>